import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Breadcrumbs, Link, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

import MainLayout from '../../../layout/MainLayout';
import 'reactflow/dist/style.css';

import CohortToolbar from './components/CohortToolbar';
import CriteriaEditorPanel from './components/CriteriaEditorPanel';
import DeleteNodeDialog from './components/DeleteNodeDialog';
import KeyboardShortcutsDialog from './components/KeyboardShortcutsDialog';
import FeedbackSnackbar from './components/FeedbackSnackbar';
import WelcomeTourAlert from './components/WelcomeTourAlert';
import CohortGraphCanvas from './components/CohortGraphCanvas';
import { GraphStateProvider, useGraphState } from './graph/state/GraphStateContext';
import { GRAPH_ACTIONS, createEmptyGraphState } from './graph/state/graphModel';
import { useCohortBuilderController } from './hooks/useCohortBuilderController';
import {
  createCohort,
  deleteCohort,
  duplicateCohort,
  getCohort,
  listCohorts,
  saveCohortGraph,
  getCohortQuery,
  downloadCohortQuery,
} from '../../../services/cohortService';

const replaceCohortInTree = (cohorts, replacement) =>
  (cohorts || []).map((cohort) => {
    if (cohort.id === replacement.id) {
      return replacement;
    }
    if (!Array.isArray(cohort.children) || cohort.children.length === 0) {
      return cohort;
    }
    return {
      ...cohort,
      children: replaceCohortInTree(cohort.children, replacement),
    };
  });

const CohortGraphBuilderContent = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { graphState, dispatchGraph } = useGraphState();
  const [savedCohorts, setSavedCohorts] = useState([]);
  const [isLoadingCohorts, setIsLoadingCohorts] = useState(true);
  const [isPersistingCohort, setIsPersistingCohort] = useState(false);
  const [activeCohortId, setActiveCohortId] = useState(null);

  const loadGraphDetail = useCallback(
    (detail, { openGraph = true } = {}) => {
      if (!detail?.graph) {
        dispatchGraph({
          type: GRAPH_ACTIONS.SET_GRAPH,
          payload: createEmptyGraphState('Saved Cohort Workspace'),
        });
        setActiveCohortId(null);
        return;
      }

      dispatchGraph({
        type: GRAPH_ACTIONS.SET_GRAPH,
        payload: detail.graph,
      });
      setActiveCohortId(detail.id);
      return openGraph ? detail.graph.rootNodeId : null;
    },
    [dispatchGraph],
  );

  const handlePersistGraph = useCallback(
    async (currentGraphState) => {
      if (!activeCohortId) {
        throw new Error('Create or open a saved cohort before saving.');
      }

      setIsPersistingCohort(true);
      try {
        const savedDetail = await saveCohortGraph(activeCohortId, currentGraphState);
        setSavedCohorts((current) => replaceCohortInTree(current, savedDetail));
        loadGraphDetail(savedDetail, { openGraph: false });
      } finally {
        setIsPersistingCohort(false);
      }
    },
    [activeCohortId, loadGraphDetail],
  );

  const {
    importFileInputRef,
    shortcutsOpen,
    setShortcutsOpen,
    feedback,
    handleCloseFeedback,
    showFeedback,
    showWelcomeTour,
    dismissWelcomeTour,
    editingNode,
    editLabel,
    editDescription,
    editStatus,
    editIncludeCriteria,
    editExcludeCriteria,
    editParameterListName,
    criteriaOperators,
    setEditLabel,
    setEditDescription,
    setEditStatus,
    closeEdit,
    saveEdit,
    addCriterion,
    updateCriterion,
    deleteCriterion,
    parameterLists,
    parameterListsLoading,
    parameterListsError,
    loadParameterLists,
    handleInsertParameterList,
    listMembersById,
    listMembersErrorById,
    expandingListId,
    loadParameterListMembers,
    handleInsertParameterListItem,
    deleteDialog,
    closeDeleteDialog,
    confirmDeleteNode,
    isExecuting,
    executionProgress,
    executionTargetNodeId,
    executionMode,
    executionBackend,
    setExecutionBackend,
    executionLogs,
    showExecutionLog,
    setShowExecutionLog,
    graphStatistics,
    lastExecutionLabel,
    breakpointNodeId,
    breakpointNodeLabel,
    graphLastError,
    historyPastLength,
    historyFutureLength,
    layoutDirection,
    layoutVersion,
    viewMode,
    selectedNodeId,
    setLayoutDirection,
    setViewMode,
    setSelectedNodeId,
    requestAutoFit,
    handleGraphInit,
    nodes,
    edges,
    nodeTypes,
    loadDemo,
    generateLargeDemo,
    handleAutoLayout,
    executeGraph,
    executeToBreakpoint,
    handleTriggerImport,
    exportJson,
    exportPng,
    exportSvg,
    clearGraph,
    undo,
    redo,
    openEdit,
    addChild,
    duplicateNode,
    requestDeleteNode,
    handleImportFile,
  } = useCohortBuilderController({
    graphState,
    dispatchGraph,
    onPersistGraph: handlePersistGraph,
    cohortId: activeCohortId,
  });

  const refreshSavedCohorts = useCallback(async () => {
    setIsLoadingCohorts(true);
    try {
      const payload = await listCohorts();
      setSavedCohorts(payload.results || []);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load saved cohorts.';
      showFeedback(message, 'error');
      setSavedCohorts([]);
    } finally {
      setIsLoadingCohorts(false);
    }
  }, [showFeedback]);

  useEffect(() => {
    refreshSavedCohorts();
  }, [refreshSavedCohorts]);

  const handleOpenSavedCohort = useCallback(
    async (cohortId) => {
      setIsPersistingCohort(true);
      try {
        if (activeCohortId && viewMode === 'graph' && activeCohortId !== cohortId) {
          await handlePersistGraph(graphState);
          await refreshSavedCohorts();
        }

        const detail = await getCohort(cohortId);
        const rootNodeId = loadGraphDetail(detail);
        setSelectedNodeId(rootNodeId);
        setViewMode('graph');
        requestAutoFit({
          padding: 0.42,
          duration: 480,
          maxZoom: 0.88,
        });
        showFeedback(`Opened "${detail.name}" in graph view.`, 'success');
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to open cohort.';
        showFeedback(message, 'error');
      } finally {
        setIsPersistingCohort(false);
      }
    },
    [
      activeCohortId,
      graphState,
      handlePersistGraph,
      loadGraphDetail,
      refreshSavedCohorts,
      requestAutoFit,
      setSelectedNodeId,
      setViewMode,
      showFeedback,
      viewMode,
    ],
  );

  const handleCreateSavedCohort = useCallback(async () => {
    setIsPersistingCohort(true);
    try {
      const detail = await createCohort({ name: 'New Cohort' });
      await refreshSavedCohorts();
      const rootNodeId = loadGraphDetail(detail);
      setSelectedNodeId(rootNodeId);
      setViewMode('graph');
      requestAutoFit({
        padding: 0.42,
        duration: 480,
        maxZoom: 0.88,
      });
      showFeedback('New cohort created and loaded in graph view.', 'success');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create cohort.';
      showFeedback(message, 'error');
    } finally {
      setIsPersistingCohort(false);
    }
  }, [loadGraphDetail, refreshSavedCohorts, requestAutoFit, setSelectedNodeId, setViewMode, showFeedback]);

  const handleBackToCohortList = useCallback(async () => {
    try {
      if (activeCohortId) {
        await handlePersistGraph(graphState);
        await refreshSavedCohorts();
      }
      setSelectedNodeId(null);
      setViewMode('list');
      showFeedback(
        activeCohortId
          ? 'Cohort saved and returned to the saved cohort list.'
          : 'Returned to the saved cohort list.',
        'success',
      );
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to save the cohort before leaving graph view.';
      showFeedback(message, 'error');
    }
  }, [
    activeCohortId,
    graphState,
    handlePersistGraph,
    refreshSavedCohorts,
    setSelectedNodeId,
    setViewMode,
    showFeedback,
  ]);

  const handleSetViewMode = useCallback(
    async (nextViewMode) => {
      if (!nextViewMode || nextViewMode === viewMode) return;
      if (nextViewMode === 'list') {
        await handleBackToCohortList();
        return;
      }
      if (!activeCohortId) return;

      setViewMode('graph');
      if (!selectedNodeId && graphState.rootNodeId) {
        setSelectedNodeId(graphState.rootNodeId);
      }
      requestAutoFit({
        padding: 0.42,
        duration: 420,
        maxZoom: 0.88,
      });
    },
    [
      activeCohortId,
      graphState.rootNodeId,
      handleBackToCohortList,
      requestAutoFit,
      selectedNodeId,
      setSelectedNodeId,
      setViewMode,
      viewMode,
    ],
  );

  const handleOpenGraphView = useCallback(
    (nodeId) => {
      setViewMode('graph');
      setSelectedNodeId(nodeId || graphState.rootNodeId || null);
      requestAutoFit({
        nodes: nodeId ? [{ id: nodeId }] : undefined,
        padding: nodeId ? 0.5 : 0.42,
        duration: 420,
        maxZoom: nodeId ? 0.92 : 0.88,
      });
    },
    [graphState.rootNodeId, requestAutoFit, setSelectedNodeId, setViewMode],
  );

  const handleDuplicateSavedCohort = useCallback(
    async (cohortId) => {
      setIsPersistingCohort(true);
      try {
        const detail = await duplicateCohort(cohortId);
        await refreshSavedCohorts();
        showFeedback(`Duplicated "${detail.name}" in the saved cohort list.`, 'success');
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to duplicate cohort.';
        showFeedback(message, 'error');
      } finally {
        setIsPersistingCohort(false);
      }
    },
    [refreshSavedCohorts, showFeedback],
  );

  const handleLoadDemo = useCallback(() => {
    const changed = loadDemo();
    if (changed) setViewMode('graph');
  }, [loadDemo, setViewMode]);

  const handleGenerateLargeDemo = useCallback(() => {
    const changed = generateLargeDemo();
    if (changed) setViewMode('graph');
  }, [generateLargeDemo, setViewMode]);

  const handleDeleteSavedCohort = useCallback(
    async (cohortId) => {
      const cohort = savedCohorts.find(item => item.id === cohortId);
      const confirmed = window.confirm(
        `Delete "${cohort?.name || 'this cohort'}" and all of its sub-cohorts?`,
      );
      if (!confirmed) return;

      setIsPersistingCohort(true);
      try {
        await deleteCohort(cohortId);
        if (activeCohortId === cohortId) {
          dispatchGraph({
            type: GRAPH_ACTIONS.SET_GRAPH,
            payload: createEmptyGraphState('Saved Cohort Workspace'),
          });
          setActiveCohortId(null);
          setSelectedNodeId(null);
          setViewMode('list');
        }
        await refreshSavedCohorts();
        showFeedback('Cohort deleted from the database.', 'success');
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to delete cohort.';
        showFeedback(message, 'error');
      } finally {
        setIsPersistingCohort(false);
      }
    },
    [activeCohortId, dispatchGraph, refreshSavedCohorts, savedCohorts, setSelectedNodeId, setViewMode, showFeedback],
  );

  // #796: Abfrage anzeigen und herunterladen. Eigener Zustand, nicht am
  // Ausfuehrungszustand haengend -- der Endpunkt uebersetzt nur und laeuft
  // auch dann, wenn `execute` mangels Datenquelle mit 503 antwortet. Das
  // Backend fuer die Abfrage ist ebenfalls eigenstaendig (Review Runde 1,
  // B1): der Ausfuehrungs-Umschalter laesst `openehr` bis #E7.5 gar nicht
  // erst zu, die Abfrage-Uebersetzung kann es aber schon heute.
  const [queryResult, setQueryResult] = useState(null);
  const [queryError, setQueryError] = useState(null);
  const [isLoadingQuery, setIsLoadingQuery] = useState(false);
  const [queryBackend, setQueryBackend] = useState('omop');

  // Review Runde 2, B5: `activeCohortId` allein reicht als Vergleichswert
  // nach einem `await` nicht -- der Handler haelt in seiner eigenen Closure
  // den Stand von *vor* dem Aufruf, nicht den aktuellen. Ein Ref traegt immer
  // den neuesten Wert, unabhaengig davon, welche Closure gerade liest.
  const activeCohortIdRef = useRef(activeCohortId);
  useEffect(() => {
    activeCohortIdRef.current = activeCohortId;
  }, [activeCohortId]);

  // Review Runde 1, B3: ein Kohortenwechsel darf nicht die Abfrage der
  // vorigen Kohorte auf dem Schirm lassen -- ein Download wuerde sonst eine
  // andere Kohorte laden als die angezeigte Abfrage gehoert.
  useEffect(() => {
    setQueryResult(null);
    setQueryError(null);
  }, [activeCohortId]);

  const handleShowQuery = useCallback(async (backend) => {
    if (!activeCohortId) return;
    const angefragteKohorte = activeCohortId;
    setIsLoadingQuery(true);
    setQueryError(null);
    try {
      const ergebnis = await getCohortQuery(activeCohortId, { backend });
      // Review Runde 2, B5: zwischenzeitlich kann die Kohorte gewechselt
      // haben -- eine spaet eintreffende Antwort fuer die alte Kohorte darf
      // die Abfrage der inzwischen aktiven Kohorte nicht ueberschreiben.
      if (activeCohortIdRef.current !== angefragteKohorte) return;
      if (ergebnis.source === 'error') {
        setQueryResult(null);
        // Review Runde 1, B4: `unuebersetzbar` gehoert mit angezeigt, nicht
        // nur `detail' -- sonst bleibt ungenannt, welches Kriterium betroffen ist.
        setQueryError({ detail: ergebnis.detail, unuebersetzbar: ergebnis.unuebersetzbar });
      } else {
        setQueryResult(ergebnis);
      }
    } catch (fehler) {
      if (activeCohortIdRef.current !== angefragteKohorte) return;
      setQueryResult(null);
      setQueryError({ detail: fehler.message || 'Query could not be loaded.', unuebersetzbar: [] });
    } finally {
      if (activeCohortIdRef.current === angefragteKohorte) setIsLoadingQuery(false);
    }
  }, [activeCohortId]);

  const handleDownloadQuery = useCallback(async (backend) => {
    if (!activeCohortId) return;
    const angefragteKohorte = activeCohortId;
    setIsLoadingQuery(true);
    setQueryError(null);
    try {
      const ergebnis = await downloadCohortQuery(activeCohortId, { backend });
      // Review Runde 2, B5: derselbe Schutz wie bei handleShowQuery -- ein
      // Kohortenwechsel waehrend des Downloads darf weder die Fehleranzeige
      // noch die heruntergeladene Datei der inzwischen aktiven Kohorte
      // zuschreiben.
      if (activeCohortIdRef.current !== angefragteKohorte) return;
      if (ergebnis.source === 'error') {
        // 422 heisst "diese Datei waere unvollstaendig" (#794) -- die
        // Begruendung UND das betroffene Kriterium gehoeren angezeigt, keine
        // leere Datei ausgeliefert (Review Runde 1, B4).
        setQueryError({ detail: ergebnis.detail, unuebersetzbar: ergebnis.unuebersetzbar });
        return;
      }
      const blob = new Blob([ergebnis.text], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const anker = document.createElement('a');
      anker.href = url;
      anker.download = ergebnis.dateiname || `cohort-${angefragteKohorte}.txt`;
      document.body.appendChild(anker);
      anker.click();
      document.body.removeChild(anker);
      URL.revokeObjectURL(url);
    } catch (fehler) {
      if (activeCohortIdRef.current !== angefragteKohorte) return;
      setQueryError({ detail: fehler.message || 'Query could not be downloaded.', unuebersetzbar: [] });
    } finally {
      if (activeCohortIdRef.current === angefragteKohorte) setIsLoadingQuery(false);
    }
  }, [activeCohortId]);


  return (
    <MainLayout>
      <Box sx={{ p: 3 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link underline="hover" color="inherit" sx={{ cursor: 'pointer' }} onClick={() => navigate('/')}>
            Home
          </Link>
          <Typography color="text.primary">Cohort Builder</Typography>
        </Breadcrumbs>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
            Cohort Builder
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Start from the saved cohort list, then open a cohort in graph view to edit its hierarchy.
          </Typography>
        </Box>

        <WelcomeTourAlert
          open={showWelcomeTour}
          onOpenShortcuts={() => setShortcutsOpen(true)}
          onDismiss={dismissWelcomeTour}
        />

        <CohortToolbar
          nodesCount={nodes.length}
          isExecuting={isExecuting || isPersistingCohort}
          breakpointNodeId={breakpointNodeId}
          breakpointNodeLabel={breakpointNodeLabel}
          historyPastLength={historyPastLength}
          historyFutureLength={historyFutureLength}
          layoutDirection={layoutDirection}
          viewMode={viewMode}
          hasActiveCohort={Boolean(activeCohortId)}
          graphStatistics={graphStatistics}
          lastExecutionLabel={lastExecutionLabel}
          executionProgress={executionProgress}
          executionTargetNodeId={executionTargetNodeId}
          executionMode={executionMode}
          executionBackend={executionBackend}
          onSetExecutionBackend={setExecutionBackend}
          showExecutionLog={showExecutionLog}
          executionLogs={executionLogs}
          queryResult={queryResult}
          queryError={queryError}
          isLoadingQuery={isLoadingQuery}
          queryBackend={queryBackend}
          onSetQueryBackend={setQueryBackend}
          onShowQuery={handleShowQuery}
          onDownloadQuery={handleDownloadQuery}
          graphLastError={graphLastError}
          onCreateRootNode={handleCreateSavedCohort}
          onLoadDemo={handleLoadDemo}
          onGenerateLargeDemo={handleGenerateLargeDemo}
          onAutoLayout={handleAutoLayout}
          onExecuteGraph={executeGraph}
          onExecuteToBreakpoint={executeToBreakpoint}
          onSaveGraph={() => handlePersistGraph(graphState)}
          onImportJson={handleTriggerImport}
          onExportJson={exportJson}
          onExportPng={exportPng}
          onExportSvg={exportSvg}
          onClearGraph={clearGraph}
          onUndo={undo}
          onRedo={redo}
          onOpenShortcuts={() => setShortcutsOpen(true)}
          onToggleExecutionLog={() => setShowExecutionLog((current) => !current)}
          onSetLayoutDirection={setLayoutDirection}
          onSetViewMode={handleSetViewMode}
        />

        <CohortGraphCanvas
          nodes={nodes}
          edges={edges}
          viewMode={viewMode}
          graphState={graphState}
          savedCohorts={savedCohorts}
          activeCohortId={activeCohortId}
          selectedNodeId={selectedNodeId}
          isLoadingCohorts={isLoadingCohorts}
          breakpointNodeId={breakpointNodeId}
          isExecuting={isExecuting || isPersistingCohort}
          layoutDirection={layoutDirection}
          layoutVersion={layoutVersion}
          nodeTypes={nodeTypes}
          onInit={handleGraphInit}
          onNodeClick={setSelectedNodeId}
          onToggleExpanded={() => {}}
          onOpenCohort={handleOpenSavedCohort}
          onCreateRootNode={handleCreateSavedCohort}
          onDuplicateCohort={handleDuplicateSavedCohort}
          onDeleteSavedCohort={handleDeleteSavedCohort}
          onLoadDemo={handleLoadDemo}
          onOpenGraphView={handleOpenGraphView}
          onEditNode={openEdit}
          onAddChild={addChild}
          onDuplicateNode={duplicateNode}
          onDeleteNode={requestDeleteNode}
        />

        <CriteriaEditorPanel
          open={Boolean(editingNode)}
          isSmallScreen={isSmallScreen}
          editLabel={editLabel}
          editDescription={editDescription}
          editStatus={editStatus}
          editIncludeCriteria={editIncludeCriteria}
          editExcludeCriteria={editExcludeCriteria}
          editParameterListName={editParameterListName}
          criteriaOperators={criteriaOperators}
          parameterLists={parameterLists}
          parameterListsLoading={parameterListsLoading}
          parameterListsError={parameterListsError}
          onClose={closeEdit}
          onSave={saveEdit}
          onChangeLabel={setEditLabel}
          onChangeDescription={setEditDescription}
          onChangeStatus={setEditStatus}
          onAddCriterion={addCriterion}
          onUpdateCriterion={updateCriterion}
          onDeleteCriterion={deleteCriterion}
          onLoadParameterLists={loadParameterLists}
          onInsertParameterList={handleInsertParameterList}
          listMembersById={listMembersById}
          listMembersErrorById={listMembersErrorById}
          expandingListId={expandingListId}
          onLoadParameterListMembers={loadParameterListMembers}
          onInsertParameterListItem={handleInsertParameterListItem}
        />

        <DeleteNodeDialog
          open={deleteDialog.open}
          isSmallScreen={isSmallScreen}
          nodeLabel={deleteDialog.nodeLabel}
          descendantCount={deleteDialog.descendantIds.length}
          onCancel={closeDeleteDialog}
          onConfirm={confirmDeleteNode}
        />

        <KeyboardShortcutsDialog
          open={shortcutsOpen}
          isSmallScreen={isSmallScreen}
          onClose={() => setShortcutsOpen(false)}
        />

        <FeedbackSnackbar
          open={feedback.open}
          severity={feedback.severity}
          message={feedback.message}
          onClose={handleCloseFeedback}
        />

        <input
          ref={importFileInputRef}
          type="file"
          accept="application/json"
          style={{ display: 'none' }}
          aria-label="Import graph JSON file input"
          onChange={handleImportFile}
        />
      </Box>
    </MainLayout>
  );
};

const CohortGraphBuilder = () => {
  const initialGraphState = useMemo(() => createEmptyGraphState('Saved Cohort Workspace'), []);

  return (
    <GraphStateProvider initialState={initialGraphState}>
      <CohortGraphBuilderContent />
    </GraphStateProvider>
  );
};

export default CohortGraphBuilder;
